import {
  PNL_LOOKBACKS,
  POSITION_SUB_ACCOUNT,
  PnLTagEnum,
  getAgGridColId,
  isPnlLookbackEnum,
  type Column,
  type PnlLookbackEnum,
  type Position,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { GridState, StateUpdatedEvent } from 'ag-grid-community';
import { isEqual, sortBy } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { useDisplaySettings } from '../../../../providers/DisplaySettingsProvider';
import { POSITIONS_BLOTTER_CONVERSION_TOLERANCE } from '../tokens';
import { useRequestEndOfDay } from './useRequestEndOfDay';

export { customEODToRequestEOD, dateToRequestEOD } from './customEODToRequestEOD';

export interface SubAccountPositionsRequest extends WebsocketRequest {
  ShowZeroBalances: boolean;
  EquivalentCurrency: string;
  Tolerance: string;
  EndOfDay?: string;
  PnLLookbacks?: PnlLookbackEnum[];
  Tags?: string[];
  FlattenRollups?: boolean;
}

export interface UseSubAccountPositionsBlotterRequestParams {
  requestTag: string;
  showZeroBalances: boolean;
  initialGridState: GridState | undefined; // There might not be any initialGridState
  initialColumns: Column[];
  /**
   * Pass true here when your blotter is in hierarchical mode. This will cause the request to set FlattenRollups: true.
   * When this is done, the backend will resolve all passed rollups in the filter to their underlying books and only return positions
   * for those resolved books.
   */
  hierarchical?: boolean;
}

/**
 * This hook encapsulates the logic of the SubAccountPositionsBlotter request management
 *
 * This request management is more complex in comparison to other blotters seeing as the request
 * is a function of the columns visible. As columns are changed, the request is (maybe) amended.
 *
 * Returns the request itself (to be used with `useConstant` in order to get an `initialRequest`),
 * together with an onColumnsChanged function which the implementer needs to call whenever the associated
 * BlotterTable's own onColumnsChanged runs.
 */
export const useSubAccountPositionsBlotterRequest = ({
  initialGridState,
  initialColumns,
  requestTag,
  showZeroBalances,
  hierarchical,
}: UseSubAccountPositionsBlotterRequestParams) => {
  const { homeCurrency } = useDisplaySettings();
  const resolveEndOfDay = useRequestEndOfDay();

  const [request, setRequest] = useState<SubAccountPositionsRequest>(() => {
    const endOfDay = resolveEndOfDay(undefined);
    return {
      name: POSITION_SUB_ACCOUNT,
      tag: requestTag,
      ShowZeroBalances: showZeroBalances,
      EquivalentCurrency: homeCurrency,
      Tolerance: POSITIONS_BLOTTER_CONVERSION_TOLERANCE,
      ...(endOfDay !== undefined && { EndOfDay: endOfDay }),
      PnLLookbacks: getRequestPnlLookbacks(initialColumns, initialGridState),
      Tags: getRequestPnlTags(initialColumns, initialGridState),
      FlattenRollups: hierarchical,
    };
  });

  // useEffect to react to changes in request dependencies, update whenever a change occurs
  useEffect(() => {
    setRequest(currentRequest => {
      const endOfDay = resolveEndOfDay(currentRequest.EndOfDay);
      const newRequest: SubAccountPositionsRequest = {
        ...currentRequest,
        ...(endOfDay !== undefined && { EndOfDay: endOfDay }),
        ShowZeroBalances: showZeroBalances,
        FlattenRollups: hierarchical,
      };
      if (isEqual(currentRequest, newRequest)) {
        return currentRequest;
      }

      return newRequest;
    });
  }, [resolveEndOfDay, showZeroBalances, hierarchical]);

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      // Check the PnLLookbacks and see if we should amend the request based on the new visible columns
      setRequest(currentRequest => {
        const newRequest: SubAccountPositionsRequest = {
          ...currentRequest,
          PnLLookbacks: getRequestPnlLookbacks(initialColumns, event.state),
          Tags: getRequestPnlTags(initialColumns, event.state),
        };
        if (isEqual(currentRequest, newRequest)) {
          return currentRequest;
        }

        return newRequest;
      });
    },
    [initialColumns]
  );

  return { request, onStateUpdated };
};

/**
 * Convert an array of columns into an array of the necessary PnLLookbackEnums we need in order to populate these visible columns.
 *
 * For Lookbacks, each column (currently) corresponds to one PnLLookbackEnum. Eg one column is "24H", another "WTD", etc.
 * Multiple visible columns can share the same lookback field — dedupe, then order by {@link PNL_LOOKBACKS} (stable product order, independent of column order).
 */
export function getRequestPnlLookbacks(initialColumns: Column[], gridState: GridState | undefined): PnlLookbackEnum[] {
  const gridStateHiddenColIDs = gridState?.columnVisibility?.hiddenColIds
    ? new Set<string>(gridState?.columnVisibility?.hiddenColIds)
    : undefined;

  const fromColumns = initialColumns
    .filter(column => column.field?.includes('PnLLookbacks.') && !isColumnHidden(column, gridStateHiddenColIDs))
    .map(lookbackField => lookbackField.field?.split('.')[1])
    .filter(isPnlLookbackEnum);
  const unique = [...new Set(fromColumns)];
  return sortBy(unique, lb => {
    const i = PNL_LOOKBACKS.indexOf(lb);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  });
}

/** Extra PnLTag IDs are the extra column fields which, when visible to the user, should result in us creating a PnlTags request amendment */
const EXTRA_PNLTAG_COL_FIELDS: string[] = ['eodTotalPnL', 'eodTotalPnLEquivalent'] satisfies (keyof Position)[];

/**
 * Checks if there are any PnLTag columns visible to the user. If there are, then we return the required PnLTags for the request.
 */
export function getRequestPnlTags(initialColumns: Column[], gridState: GridState | undefined): string[] {
  const gridStateHiddenColIDs = gridState?.columnVisibility?.hiddenColIds
    ? new Set<string>(gridState?.columnVisibility?.hiddenColIds)
    : undefined;

  const somePnLTagColumnVisible = initialColumns.some(column => {
    if (isColumnHidden(column, gridStateHiddenColIDs) || !column.field) {
      return false;
    }

    return column.field?.includes('PnLTags.') || EXTRA_PNLTAG_COL_FIELDS.some(key => column.field?.includes(key));
  });

  // We currently only have one PnLTagEnum: EndOfDay. When/if we add in more, this function will need to be made smarter.
  return somePnLTagColumnVisible ? [PnLTagEnum.EndOfDay] : [];
}

function isColumnHidden(initialColumn: Column, gridStateHiddenColIDs: Set<string> | undefined): boolean {
  const colID = getAgGridColId(initialColumn);

  // If there is some state persisted describing which colIDs are hidden, we purely rely on its contents
  if (gridStateHiddenColIDs != null) {
    return gridStateHiddenColIDs.has(colID);
  }

  // Else if there is no grid state, we rely on the initial column definitions. This should only happen on the first mount of a brand new blotter.
  if (initialColumn.hide || initialColumn.initialHide) {
    return true;
  }

  // And if nobody is saying that we're hidden, then we are visible
  return false;
}
